import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Target, Trophy, Users } from 'lucide-react';
import { analyticsService } from '@/services/analyticsService';

interface TeamPerformanceData {
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  trend: 'up' | 'down' | 'neutral';
  recentGames: Array<{
    date: string;
    opponent: string;
    result: 'W' | 'L' | 'D';
    score: string;
  }>;
  monthlyPerformance: Array<{
    month: string;
    winRate: number;
  }>;
}

const TeamPerformanceChart = () => {
  const [data, setData] = useState<TeamPerformanceData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamPerformance();
  }, []);

  const fetchTeamPerformance = async () => {
    try {
      const performanceData = await analyticsService.getTeamPerformanceData();
      setData(performanceData as TeamPerformanceData);
    } catch (error) {
      console.error('Error in fetchTeamPerformance:', error);
      setData({
        wins: 0,
        losses: 0, 
        draws: 0,
        winRate: 0,
        trend: 'neutral' as const,
        recentGames: [],
        monthlyPerformance: []
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Team Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const totalGames = data.wins + data.losses + data.draws;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Performance Metrics */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Wins</p>
                <p className="text-2xl font-bold text-green-600">{data.wins}</p>
              </div>
              <Trophy className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Losses</p>
                <p className="text-2xl font-bold text-red-600">{data.losses}</p>
              </div>
              <Target className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Draws</p>
                <p className="text-2xl font-bold text-yellow-600">{data.draws}</p>
              </div>
              <Users className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Win Rate</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{data.winRate}%</p>
                  {data.trend === 'up' ? (
                    <TrendingUp className="h-4 w-4 text-green-600" />
                  ) : data.trend === 'down' ? (
                    <TrendingDown className="h-4 w-4 text-red-600" />
                  ) : null}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Performance Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Win Rate Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.monthlyPerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="winRate" stroke="#8884d8" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Recent Games */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Game Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.recentGames.map((game, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Badge variant={game.result === 'W' ? 'default' : game.result === 'L' ? 'destructive' : 'secondary'}>
                    {game.result}
                  </Badge>
                  <span className="font-medium">{game.opponent}</span>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm">{game.score}</p>
                  <p className="text-xs text-muted-foreground">{new Date(game.date).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
            {data.recentGames.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No recent games found</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamPerformanceChart;